import type { ZonedDateTime } from '@internationalized/date';
import camelcaseKeys from 'camelcase-keys';
import decamelizeKeys from 'decamelize-keys';

import type { Tables, TablesInsert } from '@db-types';
import type {
  Note,
  NotesUpdate,
  NotesInsert,
  NoteWithHabit,
  NotePeriodKind,
  TypedSupabaseClient,
} from '@models';

export const createNote = async (
  supabase: TypedSupabaseClient,
  note: NotesInsert
): Promise<Note> => {
  const { data, error } = await supabase
    .from('notes')
    .insert(decamelizeKeys(note) as TablesInsert<'notes'>)
    .select()
    .single();

  if (error) {
    throw new Error(error.message);
  }

  return camelcaseKeys(data);
};

export const listNotes = async (
  supabase: TypedSupabaseClient,
  [rangeStart, rangeEnd]: [ZonedDateTime, ZonedDateTime]
): Promise<Note[]> => {
  const startDateString = rangeStart.toAbsoluteString();
  const endDateString = rangeEnd.toAbsoluteString();

  const periodNotesPromise = supabase
    .from('notes')
    .select()
    .in('period_kind', ['day', 'week', 'month'])
    .gte('period_date', startDateString)
    .lte('period_date', endDateString);

  const occurrenceNotesPromise = supabase
    .from('occurrences')
    .select('note:notes!inner(*)')
    .gte('occurred_at', startDateString)
    .lte('occurred_at', endDateString);

  const [periodResult, occurrenceResult] = await Promise.all([
    periodNotesPromise,
    occurrenceNotesPromise,
  ]);

  if (periodResult.error) {
    throw new Error(periodResult.error.message);
  }

  if (occurrenceResult.error) {
    throw new Error(occurrenceResult.error.message);
  }

  const notesMap = new Map<string, Tables<'notes'>>();

  const occurrenceNotes = occurrenceResult.data.map(({ note }) => {
    return note;
  });

  for (const note of [...periodResult.data, ...occurrenceNotes]) {
    notesMap.set(note.id, note);
  }

  return camelcaseKeys([...notesMap.values()], { deep: true });
};

export const getPeriodNote = async (
  supabase: TypedSupabaseClient,
  periodKind: NonNullable<NotePeriodKind>,
  periodDate: string
): Promise<Note | null> => {
  const { data, error } = await supabase
    .from('notes')
    .select()
    .eq('period_kind', periodKind)
    .eq('period_date', periodDate)
    .order('created_at')
    .limit(1)
    .maybeSingle();

  if (error) {
    throw new Error(error.message);
  }

  return data && camelcaseKeys(data);
};

export const updateNote = async (
  supabase: TypedSupabaseClient,
  id: Note['id'],
  note: NotesUpdate
): Promise<Note> => {
  const { data, error } = await supabase
    .from('notes')
    .update(decamelizeKeys(note))
    .eq('id', id)
    .select();

  if (error) {
    throw new Error(error.message);
  }

  return camelcaseKeys(data[0]);
};

export const destroyNote = async (
  supabase: TypedSupabaseClient,
  id: Note['id']
) => {
  const { error } = await supabase.from('notes').delete().eq('id', id);

  if (error) {
    throw new Error(error.message);
  }
};

export type ListAllNotesParams = {
  limit: number;
  page: number;
};

export const listAllNotes = async (
  supabase: TypedSupabaseClient,
  { limit, page }: ListAllNotesParams
): Promise<NoteWithHabit[]> => {
  const offset = page * limit;

  const { data, error } = await supabase
    .from('notes')
    .select(
      `
      *,
      occurrences (
        habits (
          name,
          icon_path
        )
      )
    `
    )
    .order('created_at', { ascending: false })
    .range(offset, offset + limit - 1);

  if (error) {
    throw new Error(error.message);
  }

  return data.map((note) => {
    const habit = note.occurrences?.habits ?? null;
    const { occurrences: _occurrences, ...noteWithoutOccurrences } = note;

    return camelcaseKeys({
      ...noteWithoutOccurrences,
      habit: habit
        ? {
            iconPath: habit.icon_path,
            name: habit.name,
          }
        : null,
    });
  });
};
